import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Your Stripe publishable key
const stripePromise = loadStripe('pk_live_51MEnMzLkQLIMf5a6jgU2hzzEdYrg5bulymODYwo9Kh4G78WbO9whk4BBQhMjdPtGU7je1UT83pV3eyrLrkJfI9xC00lmnZ7wrB');

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      backgroundColor: '#374151',
      '::placeholder': {
        color: '#9CA3AF',
      },
      iconColor: '#C4B5FD',
    },
    invalid: {
      color: '#EF4444',
      iconColor: '#EF4444',
    },
  },
  hidePostalCode: false,
  disableLink: true,
};

function PaymentForm({ 
  clientSecret, 
  amount, 
  songName, 
  artistName, 
  musicianName, 
  customerName,
  onSuccess, 
  onError,
  onCancel 
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      // Confirm the payment with Stripe
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: customerName,
          },
        }
      });

      if (error) {
        console.error('Payment failed:', error);
        setPaymentError(error.message);
        onError(error);
      } else if (paymentIntent && paymentIntent.status === 'requires_capture') {
        // Payment authorized successfully (manual capture)
        console.log('Payment authorized:', paymentIntent.id);
        onSuccess(paymentIntent);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setPaymentError('An unexpected error occurred. Please try again.');
      onError(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCardChange = (event) => {
    setCardComplete(event.complete);
    if (event.error) {
      setPaymentError(event.error.message);
    } else {
      setPaymentError(null);
    }
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Payment Summary */}
      <div style={{
        backgroundColor: '#374151',
        border: '2px solid #4B5563',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '32px'
      }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: 'bold',
          color: 'white',
          marginBottom: '16px',
          margin: '0 0 16px 0'
        }}>
          Payment Details
        </h3>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#C4B5FD', fontSize: '16px' }}>Song:</span>
            <span style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
              {songName} by {artistName}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#C4B5FD', fontSize: '16px' }}>Performer:</span>
            <span style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
              {musicianName}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#C4B5FD', fontSize: '16px' }}>Customer:</span>
            <span style={{ color: 'white', fontSize: '16px', fontWeight: 'bold' }}>
              {customerName}
            </span>
          </div>
          <div style={{
            borderTop: '2px solid #4B5563',
            paddingTop: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ color: '#FCD34D', fontSize: '18px', fontWeight: 'bold' }}>Total Authorization:</span>
            <span style={{ color: '#FCD34D', fontSize: '20px', fontWeight: 'bold' }}>
              ${(amount / 100).toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Payment Form */}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '24px' }}>
          <label style={{
            display: 'block',
            color: '#C4B5FD',
            fontSize: '14px',
            fontWeight: 'bold',
            marginBottom: '12px'
          }}>
            Card Information
          </label>
          
          <div style={{
            backgroundColor: '#374151',
            border: '2px solid #4B5563',
            borderRadius: '8px',
            padding: '16px',
            transition: 'border-color 0.3s ease'
          }}>
            <CardElement
              options={cardElementOptions}
              onChange={handleCardChange}
            />
          </div>
        </div>

        {/* Error Display */}
        {paymentError && (
          <div style={{
            backgroundColor: '#7F1D1D',
            border: '2px solid #EF4444',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <span style={{ color: '#FEE2E2', fontSize: '16px' }}>{paymentError}</span>
            </div>
          </div>
        )}

        {/* Tip Buffer Notice */}
        <div style={{
          backgroundColor: '#312E81',
          border: '2px solid #6366F1',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '16px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>💡</span>
            <div>
              <h4 style={{
                color: '#C7D2FE',
                fontSize: '14px',
                fontWeight: 'bold',
                marginBottom: '6px',
                margin: '0 0 6px 0'
              }}>
                Smart Tipping System
              </h4>
              <p style={{
                color: '#C7D2FE',
                fontSize: '13px',
                margin: 0,
                lineHeight: '1.4'
              }}>
                We hold ${(amount / 100).toFixed(2)} on your card, but you'll only be charged the base amount plus any tip you choose when rating the performance.
              </p>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div style={{
          backgroundColor: '#1E3A8A',
          border: '2px solid #3B82F6',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>ℹ️</span>
            <div>
              <h4 style={{
                color: '#BFDBFE',
                fontSize: '16px',
                fontWeight: 'bold',
                marginBottom: '8px',
                margin: '0 0 8px 0'
              }}>
                Payment Authorization Only
              </h4>
              <p style={{
                color: '#BFDBFE',
                fontSize: '14px',
                lineHeight: '1.5',
                margin: 0
              }}>
                Your card will be authorized but NOT charged yet. Payment will only be processed 
                after you rate the performance. If you're not satisfied, you can cancel at no charge.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <button
            type="submit"
            disabled={!stripe || !cardComplete || isProcessing}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: (!stripe || !cardComplete || isProcessing) ? '#4B5563' : '#10B981',
              border: `3px solid ${(!stripe || !cardComplete || isProcessing) ? '#6B7280' : '#059669'}`,
              borderRadius: '12px',
              color: 'white',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: (!stripe || !cardComplete || isProcessing) ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isProcessing ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Authorizing Payment...
              </>
            ) : (
              <>
                🔒 Authorize Payment - ${(amount / 100).toFixed(2)}
              </>
            )}
          </button>

          <button
            type="button"
            onClick={onCancel}
            disabled={isProcessing}
            style={{
              width: '100%',
              padding: '14px',
              backgroundColor: 'transparent',
              border: '2px solid #6B7280',
              borderRadius: '12px',
              color: '#9CA3AF',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => {
              if (!isProcessing) {
                e.target.style.backgroundColor = '#374151';
                e.target.style.color = 'white';
              }
            }}
            onMouseOut={(e) => {
              if (!isProcessing) {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#9CA3AF';
              }
            }}
          >
            Cancel
          </button>
        </div>
      </form>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}

// Main component that wraps the payment form with Stripe Elements provider
export default function StripePaymentForm(props) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} />
    </Elements>
  );
}